/**
 * Keyboard Shortcuts Hook
 * Provides keyboard navigation for power users
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface ShortcutHandlers {
  onSaveDraft?: () => void;
  onExport?: () => void;
  onRefresh?: () => void;
  onShowShortcuts?: () => void;
}

export const useKeyboardShortcuts = (handlers?: ShortcutHandlers) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignore shortcuts when typing in input fields
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // ?: Show keyboard shortcuts
      if (e.key === '?' && e.shiftKey) {
        e.preventDefault();
        if (handlers?.onShowShortcuts) {
          handlers.onShowShortcuts();
        }
      }

      // Alt + U: Upload
      if (e.altKey && e.key === 'u') {
        e.preventDefault();
        navigate('/upload');
      }

      // Alt + M: Allocation Matrix
      if (e.altKey && e.key === 'm') {
        e.preventDefault();
        navigate('/allocation/matrix');
      }

      // Alt + H: History
      if (e.altKey && e.key === 'h') {
        e.preventDefault();
        navigate('/history');
      }

      // Alt + R: Reports — Phase 2, not yet implemented
      // if (e.altKey && e.key === 'r') {
      //   e.preventDefault();
      //   navigate('/reports');
      // }

      // Alt + D: Dashboard
      if (e.altKey && e.key === 'd') {
        e.preventDefault();
        navigate('/dashboard');
      }

      // Ctrl + S: Save draft (on allocation matrix page)
      if (e.ctrlKey && e.key === 's') {
        if (location.pathname.includes('matrix') || location.pathname.includes('allocation')) {
          e.preventDefault();
          if (handlers?.onSaveDraft) {
            handlers.onSaveDraft();
          }
        }
      }

      // Ctrl + E: Export data
      if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        if (handlers?.onExport) {
          handlers.onExport();
        }
      }

      // F5 / Ctrl + R: Refresh data
      if (e.key === 'F5' || (e.ctrlKey && e.key === 'r')) {
        if (handlers?.onRefresh) {
          e.preventDefault();
          handlers.onRefresh();
        }
      }

      // Escape: Close dialogs/modals (handled by Material-UI by default)
      // Tab / Shift+Tab: Navigate between elements (browser default)
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate, location, handlers]);
};

// Available keyboard shortcuts
export const KEYBOARD_SHORTCUTS = [
  {
    category: 'Navigation',
    shortcuts: [
      { keys: 'Alt + U', description: 'Go to Upload page' },
      { keys: 'Alt + M', description: 'Go to Allocation Matrix' },
      { keys: 'Alt + H', description: 'Go to History' },
      // { keys: 'Alt + R', description: 'Go to Reports' }, // Phase 2
      { keys: 'Alt + D', description: 'Go to Dashboard' },
    ],
  },
  {
    category: 'Actions',
    shortcuts: [
      { keys: 'Ctrl + S', description: 'Save draft (in Allocation Matrix)' },
      { keys: 'Ctrl + E', description: 'Export data' },
      { keys: 'F5 or Ctrl + R', description: 'Refresh data' },
      { keys: 'Shift + Esc', description: 'Close dialog' },
    ],
  },
  {
    category: 'General',
    shortcuts: [
      { keys: 'Tab', description: 'Next field' },
      { keys: 'Shift + Tab', description: 'Previous field' },
      { keys: '?', description: 'Show keyboard shortcuts' },
    ],
  },
];
